import "./App.css";
import { ReclaimProofRequest } from "@reclaimprotocol/js-sdk";
import { useState, useEffect } from "react";
import { WagmiProvider, useAccount } from "wagmi";
import { config } from "./config";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import QRCode from "react-qr-code";
import VerifyProof from "./verify-proof";
import { Account } from "./account";
import { WalletOptions } from "./wallet-options";
import EligibilityScreen from "./eligiblity-screen";
import { Toaster, toast } from 'sonner';
import NotEligibleScreen from './not-eligible-screen';

const queryClient = new QueryClient();

function ConnectWallet() {
  const { isConnected } = useAccount();
  if (isConnected) return <Account />;
  return <WalletOptions />;
}

function App() {
  const [ready, setReady] = useState(false);
  const [proof, setProof] = useState({});
  const [reclaimProofRequest, setReclaimProofRequest] = useState(null);
  const [requestUrl, setRequestUrl] = useState("");
   const [statusUrl, setStatusUrl] = useState("");
  const [isEligible, setIsEligible] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const [notEligible, setNotEligible] = useState(false);

  useEffect(() => {
    async function initializeReclaim() {
      const APP_ID = "0xcF971aD412E15f14203bfE6E778Cad070b0fBdE0"; // This is an example App Id Replace it with your App Id.
      const APP_SECRET =
        "0x34278703f3778fad6e0112b9bb24f2d35056a217bc1725e84095707ee91c6319"; // This is an example App Secret Replace it with your App Secret.
      const PROVIDER_ID = "aad95818-f726-4a34-be97-8d1f47631b3d"; // This is GitHub Provider Id Replace it with the provider id you want to use.

      const proofRequest = await ReclaimProofRequest.init(
        APP_ID,
        APP_SECRET,
        PROVIDER_ID
      );
      setReclaimProofRequest(proofRequest);
    }

    initializeReclaim();
  }, []);

  async function generateVerificationRequest() {
    if (!reclaimProofRequest) {
      console.error("Reclaim Proof Request not initialized");
      return;
    }

    reclaimProofRequest.addContext(
      `user's address`,
      "for acmecorp.com on 1st january"
    );

    const url = await reclaimProofRequest.getRequestUrl();
    setRequestUrl(url);
    const status = reclaimProofRequest.getStatusUrl();
    setStatusUrl(status);

    await reclaimProofRequest.startSession({
      onSuccess: (proof) => {
        console.log("Verification success", proof);
        setReady(true);
        setProof(proof);
        // Your business logic here
      },
      onFailure: (error) => {
        console.error("Verification failed", error);
        // Your business logic here to handle the error
      },
    });
  }

    const handleVerified = (transactionHash, result) => {
    setLoadingEligibility(true);
    setTimeout(() => { 
      if (result > 1000) {
        setIsEligible(true);
        setVerificationData({ transactionHash, result });
      } else {
        setNotEligible(true);
      }
      setLoadingEligibility(false);
    }, 1200);
  };

  if (loadingEligibility) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center bg-white/80 border-4 border-yellow-300 rounded-2xl p-12 shadow-lg animate-fade-in">
          <span className="text-7xl mb-4 animate-spin-slow">🥐</span>
          <p className="text-2xl font-bold text-yellow-900">Checking your eligibility...</p>
        </div>
      </div>
    );
  }

  if (notEligible) {
    return (
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <Toaster richColors position="top-center" />
          <div className="min-h-screen bg-blue-50 p-8 flex items-center justify-center">
            <NotEligibleScreen />
          </div>
        </QueryClientProvider>
      </WagmiProvider>
    );
  }

  if (isEligible) {
    return (
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <Toaster richColors position="top-center" />
          <div className="min-h-screen bg-blue-50 p-8 flex items-center justify-center">
            <EligibilityScreen 
              transactionHash={verificationData.transactionHash}
              result={verificationData.result}
            />
          </div>
        </QueryClientProvider>
      </WagmiProvider>
    );
  }

  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <Toaster richColors position="top-center" />
   <div className="min-h-screen bg-blue-50 p-8">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-12">
              <h1 className="text-black font-bold text-5xl mb-4 transform -rotate-1">
                Prove & Mint: Verified by Reclaim
              </h1>
            </header>

            <div className="space-y-8">
              <ConnectWallet />
              
              {!requestUrl && (
                <div className="bg-orange-100 border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-center rounded-xl">
                  <h2 className="text-black font-bold text-2xl mb-4">STEP 1: CREATE CLAIM</h2>
                  <button 
                    className="bg-green-200 border-4 border-black px-8 py-4 font-bold text-black text-xl hover:bg-green-300 transition-colors shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                    onClick={generateVerificationRequest}
                  >
                    CREATE CLAIM QRCODE
                  </button>
                </div>
              )}
              
              {requestUrl && (
                <div className="bg-purple-100 border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-center rounded-xl">
                  <h2 className="text-black font-bold text-2xl mb-4">STEP 2: SCAN QR CODE</h2>
                  <div className="bg-white border-4 border-black p-4 inline-block shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                    <QRCode value={requestUrl} size={200} />
                  </div>
                  <p className="text-black font-bold mt-4">SCAN WITH YOUR PHONE</p>
                </div>
              )}
              
              {ready && (
                <div>
                  <h2 className="text-black font-bold text-2xl mb-4 text-center">STEP 3: VERIFY PROOF</h2>
                  <VerifyProof proof={proof} onVerified={handleVerified} />
                </div>
              )}
            </div>
          </div>
        </div>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;
export { toast };
